
import Locale from 'element-ui/lib/mixins/locale'

import Total from './total'
import Sizes from './sizes'
import Pager from './pager'
import Prev from './prev'
import Next from './next'
import Jumper from './jumper'

import { scrollTo } from 'scroll-js'
import { toArray, isBlank, reduce } from 'vessel/utils'

const Spacer = {
  functional: true,
  render(h) {
    const props = { grow: true }
    return h('div', { props, class: 'page-spacer' })
  }
}

const Group = {
  functional: true,
  render(h) {
    const props = { items: 'center', wrap: false }
    return h('vs-flex', { props }, [h(Prev), h(Pager), h(Next)])
  }
}

export default {
  name: 'VsPagination',
  inheritAttrs: false,
  mixins: [Locale],
  components: {
    Total,
    Sizes,
    Pager,
    Prev,
    Next,
    Jumper,
    Spacer,
    Group
  },
  provide() {
    return {
      pagination: this,
      $t: this.t
    }
  },
  props: {
    total: {
      type: Number,
      default: 0
    },
    limit: {
      type: Number,
      default: 20
    },
    page: {
      type: Number,
      default: 1
    },
    pageSizes: {
      type: Array,
      default: () => [20, 30, 50]
    },
    pagerCount: {
      type: Number,
      default: 5
    },
    defaultLimit: {
      type: Number,
      default: 20
    },
    disabled: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: true
    },
    layout: {
      type: [String, Object],
      default: 'prev, pager, next'
    },
    gapX: {
      type: [String, Object],
      default: 'sm'
    },
    gapY: {
      type: [String, Object],
      default: 'sm'
    },
    prevText: {
      type: String,
      default: null
    },
    nextText: {
      type: String,
      default: null
    },
    router: {
      type: Boolean,
      default: true
    },
    hideOnSinglePage: {
      type: [Boolean, String, Array, Object],
      default: false
    },
    scrollTop: {
      type: Object,
      default: () => ({ top: 0, behavior: 'smooth' })
    }
  },
  vessel: {
    wrapper: {
      $attrs: 'all'
    }
  },
  watch: {
    '$route.query': {
      immediate: false,
      handler: 'onRouteChange'
    },
    params: {
      immediate: true,
      handler(value) {
        this.updateQuery(value)
      }
    },
    currentPage(newValue, oldValue) {
      if (newValue != oldValue) {
        this.$emit('current-change', newValue)
      }
    },
    currentSize(newValue, oldValue) {
      if (newValue != oldValue) {
        this.$emit('size-change', newValue)
      }
    }
  },
  data() {
    return {
      currentPage: 1,
      currentSize: this.defaultLimit
    }
  },
  computed: {
    hideOnSingle() {
      return this.$screen.match(this.hideOnSinglePage)
    },
    params() {
      return { page: this.page, size: this.limit }
    },
    visible() {
      const pages = Math.ceil(this.total / this.limit)
      return this.hideOnSingle == true ? pages > 1 : true
    },
    totalPages() {
      return Math.ceil((this.total / this.currentSize) || 1)
    },
    hasPrev() {
      return this.currentPage > 1
    },
    hasNext() {
      return this.currentPage < this.totalPages
    },
    prevPage() {
      return this.hasPrev ? this.currentPage - 1 : 1
    },
    nextPage() {
      return this.hasNext ? this.currentPage + 1 : this.totalPages
    },
    inputSize() {
      return this.small ? 'mini' : 'small'
    },
    rows() {
      const layout = this.$screen.match(this.layout)
      const hidden = this.totalPages == 1 ? toArray(this.hideOnSingle) : []

      return reduce(layout, (rows, row) => {
        const regex = new RegExp(hidden.join('|'), 'g')
        const items = row.replace(regex, '')
          .replace(/\s+/g, '')
          .replace(/->/g, 'spacer')
          .replace('prev,pager,next', 'group')
          .split(',')
          .filter(item => !isBlank(item))

        const valid = items.some(item => item != 'spacer')
        valid && rows.push(items)
      })
    }
  },
  methods: {
    async updateQuery(options) {
      const { currentPage, currentSize, defaultLimit } = this

      const newSize = parseInt(options.size || currentSize)
      const newPage = parseInt(options.page || currentPage)

      this.currentPage = newPage
      this.currentSize = newSize

      const routerMode = this.router && !this.disabled
      const hasChanged = newPage != currentPage || newSize != currentSize

      if (hasChanged) {
        this.$emit('changed', { page: this.currentPage, size: this.currentSize })
      }

      if (routerMode && hasChanged) {
        const limit = newSize == defaultLimit ? undefined : newSize
        const page  = newPage == 1 ? undefined : newPage
        const query = { ...this.$route.query, page, limit }

        if (this.scrollTop) {
          await scrollTo(window, this.scrollTop)
        }

        this.$router.push({ query: query })
      }
    },
    onRouteChange({ page, limit }) {
      if (this.router) {
        this.updateQuery({ page, size: limit })
      }
    },
    updateSize(size) {
      this.updateQuery({ size, page: 1 })
    },
    updatePage(page) {
      this.updateQuery({ page })
    },
    onPrevClick() {
      this.$emit('prev-click')
      this.updatePage(this.prevPage)
    },
    onNextClick() {
      this.$emit('next-click')
      this.updatePage(this.nextPage)
    },
    onPageClick(page) {
      this.updatePage(page)
    }
  },
  created() {
    this.onRouteChange(this.$route.query)
  }
}
